/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2026 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import axios from 'axios'
import { assetsDataApiUrl } from '@/configs/apiUris/assets-data'
import { setOffset } from '@/utils/pagination'
import { setAuthorizationConfig } from '@/utils/services'
import { setUrlWithParams } from '@/utils/url'
import { getValidSearchParams, handleThrowError } from '@/utils/fetch'
import { assetsApiUrls } from '@/configs/api-urls/assets.api-urls'

const { base: apiUrlBase, revenueBreakdown } = assetsApiUrls

export const retryAsset = async (assetDataId, getAccessToken) => {
  const config = await setAuthorizationConfig(getAccessToken)

  return axios
    .post(
      assetsDataApiUrl.retry,
      { assetDataId },
      config
    )
    .then(res => res.data)
    .catch(err => console.error('Client error in fixWork', err))
}

export const getAsset = async ({ id }, getAccessToken) => {
  const config = await setAuthorizationConfig(getAccessToken)

  return axios
    .get(`${apiUrlBase}/${id}`, config)
    .then(res => res.data)
    .catch(err => handleThrowError('getAsset', err))
}

export const updateAssetCover = async ({ id, formData }, getAccessToken) => {
  const config = await setAuthorizationConfig(getAccessToken, 'multipart/form-data')

  return axios
    .put(`${apiUrlBase}/${id}/cover`, formData, config)
    .then(res => res.data)
    .catch(err => handleThrowError('updateAssetCover', err))
}

export const postAsset = async (data, getAccessToken) => {
  const config = await setAuthorizationConfig(getAccessToken)

  return axios
    .post(apiUrlBase, data, config)
    .then(res => res)
    .catch(err => handleThrowError('postAsset', err))
}

export const getAssets = async ({ searchParams = {} }, getAccessToken) => {
  const limit = parseInt(searchParams.limit) || 10
  const pagination = {
    limit,
    offset: setOffset(searchParams.page, limit)
  }

  const params = { ...getValidSearchParams(searchParams) }

  if (params.page) {
    delete params.page
  }

  const url = setUrlWithParams(apiUrlBase, { ...params, ...pagination })
  const config = await setAuthorizationConfig(getAccessToken)

  return axios
    .get(url, config)
    .then(res => res.data)
    .catch(err => handleThrowError('getAssets', err))
}

export const getAssetsAsCsv = async (getAccessToken) => {
  const url = setUrlWithParams(`${apiUrlBase}/reports/csv`)
  const config = await setAuthorizationConfig(getAccessToken)

  return axios
    .get(url, config)
    .then(res => res.data)
    .catch(err => handleThrowError('getAssetsAsCsv', err))
}

export const unlistAsset = async (id, getAccessToken) => {
  const uri = `${apiUrlBase}/${id}/unlist`
  const config = await setAuthorizationConfig(getAccessToken)

  return axios
    .put(uri, {}, config)
    .then(res => res)
    .catch(err => handleThrowError('unlistAsset', err))
}

export const getRevenueBreakdownByAssetId = async (
  { id },
  getAccessToken
) => {
  const config = await setAuthorizationConfig(getAccessToken)

  return axios
    .get(`${apiUrlBase}/${id}/${revenueBreakdown}`, config)
    .then(res => res.data)
    .catch(err => { handleThrowError('getRevenueBreakdownByAssetId', err) })
}
